import { useState, useEffect, useCallback } from 'react';
import {
  ITravelerProfile,
  IProfileArchetype,
  CreateProfileDto,
  UpdateProfileDto
} from '../../types/profile.types';
import profileService from '../../services/api/profileService';

interface UseTravelerProfilesResult {
  profiles: ITravelerProfile[];
  defaultProfile: ITravelerProfile | null;
  archetypes: IProfileArchetype[];
  loading: boolean;
  error: string | null;
  createProfile: (dto: CreateProfileDto) => Promise<ITravelerProfile>;
  updateProfile: (
    id: string,
    dto: UpdateProfileDto
  ) => Promise<ITravelerProfile>;
  deleteProfile: (id: string) => Promise<void>;
  setDefault: (id: string) => Promise<void>;
  refresh: () => void;
}

export function useTravelerProfiles(): UseTravelerProfilesResult {
  const [profiles, setProfiles] = useState<ITravelerProfile[]>([]);
  const [archetypes, setArchetypes] = useState<IProfileArchetype[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      profileService.getMyProfiles(),
      profileService.getArchetypes()
    ])
      .then(([profilesData, archetypesData]) => {
        if (!cancelled) {
          setProfiles(profilesData);
          setArchetypes(archetypesData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load profiles');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const defaultProfile = profiles.find((p) => p.isDefault) ?? null;

  const createProfile = useCallback(
    async (dto: CreateProfileDto): Promise<ITravelerProfile> => {
      const created = await profileService.createProfile(dto);
      setProfiles((prev) => [...prev, created]);
      return created;
    },
    []
  );

  const updateProfile = useCallback(
    async (id: string, dto: UpdateProfileDto): Promise<ITravelerProfile> => {
      const updated = await profileService.updateProfile(id, dto);
      setProfiles((prev) => prev.map((p) => (p._id === id ? updated : p)));
      return updated;
    },
    []
  );

  const deleteProfile = useCallback(async (id: string): Promise<void> => {
    await profileService.deleteProfile(id);
    setProfiles((prev) => prev.filter((p) => p._id !== id));
  }, []);

  const setDefault = useCallback(async (id: string): Promise<void> => {
    await profileService.setDefaultProfile(id);
    setProfiles((prev) => prev.map((p) => ({ ...p, isDefault: p._id === id })));
  }, []);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return {
    profiles,
    defaultProfile,
    archetypes,
    loading,
    error,
    createProfile,
    updateProfile,
    deleteProfile,
    setDefault,
    refresh
  };
}
